import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/command-center`;
const ALL_TABS = ['Overview', 'Usage', 'Technical', 'Support', 'Timeline', 'Notes'];
// Phase 6 — Customer Health badge colors (spec §11 4-state model).
const HEALTH_BADGE_VARIANT = { HEALTHY: 'default', ATTENTION: 'secondary', AT_RISK: 'destructive', CRITICAL: 'destructive', NOT_AVAILABLE: 'outline' };
// Sales Admin 360 = Overview, Timeline (sales/lifecycle/usage/communication
// categories) and Notes only (owner directive) — enforced server-side too,
// this just keeps the UI from offering tabs that would 403.
const SALES_ADMIN_TABS = ['Overview', 'Timeline', 'Notes'];

/** Customer 360 (spec Section 10) — one account's full picture for internal staff. */
export default function CustomerDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('Overview');

  const load = useCallback(async () => {
    try {
      const meRes = await axios.get(`${API}/me`);
      setMe(meRes.data);
      const detailRes = await axios.get(`${API}/customers/${accountId}`);
      setDetail(detailRes.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) setDenied(true);
      else toast.error('Failed to load account');
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (denied) {
    return (
      <div className="dark min-h-screen bg-background flex items-center justify-center p-6" data-testid="customer-360-denied">
        <Card className="max-w-md w-full border-destructive/40">
          <CardHeader><CardTitle className="flex items-center gap-2 text-destructive"><Lock className="h-5 w-5" /> Access Denied</CardTitle></CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/command-center')} className="w-full"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!me || !detail) {
    return (
      <div className="dark min-h-screen bg-background flex items-center justify-center" data-testid="customer-360-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canManage = new Set(me.capabilities).has('tickets_manage');
  const canAddNotes = new Set(me.capabilities).has('notes_add');
  const canEditStatus = new Set(me.capabilities).has('account_status_edit');
  const isSalesAdmin = me.user.admin_role === 'sales_admin';
  const TABS = isSalesAdmin ? SALES_ADMIN_TABS : ALL_TABS;

  return (
    <div className="dark min-h-screen bg-background text-foreground" data-testid="customer-360-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" data-testid="customer-360-back-btn" onClick={() => navigate('/command-center')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Customers
          </Button>
          {detail.is_test_data && <Badge variant="outline" className="text-amber-500 border-amber-500/50">test data</Badge>}
        </div>

        <div>
          <h1 className="text-2xl font-semibold" data-testid="customer-360-org-name">{detail.org_name || 'Unnamed program'}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {detail.sport || 'Sport not set'} · {detail.state || 'State not set'} · {detail.owner_name || 'No owner linked'} ({detail.owner_email || '—'})
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Badge data-testid="customer-360-onboarding-state">{detail.onboarding_state.replace(/_/g, ' ')} · {detail.percent_complete}%</Badge>
            <Badge variant={HEALTH_BADGE_VARIANT[detail.health.badge] || 'outline'} data-testid="customer-360-health-badge"
                   title={(detail.health.factors || []).map((f) => f.evidence).filter(Boolean).join(' · ') || undefined}>
              Health: {detail.health.badge === 'NOT_AVAILABLE' ? 'Not available yet' : detail.health.badge.replace('_', ' ')}
            </Badge>
            {detail.first_value_at && <Badge variant="secondary">First value achieved</Badge>}
            {detail.activated_at && <Badge variant="secondary">Activated</Badge>}
          </div>
          {(detail.health.factors || []).length > 0 && (
            <ul className="mt-2 space-y-0.5" data-testid="customer-360-health-factors">
              {detail.health.factors.map((f, i) => (
                <li key={i} className="text-xs text-muted-foreground">• {f.evidence}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-1 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} data-testid={`customer-360-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                      tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'Overview' && <OverviewTab detail={detail} accountId={accountId} canEditStatus={canEditStatus} onCorrected={load} />}
        {tab === 'Usage' && <UsageTab detail={detail} />}
        {tab === 'Technical' && <TechnicalTab detail={detail} />}
        {tab === 'Support' && <SupportTab accountId={accountId} canManage={canManage} />}
        {tab === 'Timeline' && <TimelineTab accountId={accountId} />}
        {tab === 'Notes' && <NotesTab accountId={accountId} canAddNotes={canAddNotes} showSalesAlert={!isSalesAdmin}
                                       alertPending={detail.sales_note_alert_pending} onAlertAck={load} />}
      </div>
    </div>
  );
}

function OverviewTab({ detail, accountId, canEditStatus, onCorrected }) {
  const [field, setField] = useState('status');
  const [newValue, setNewValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!newValue.trim() || !reason.trim()) { toast.error('New value and reason are both required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/customers/${accountId}/correct-status`, { field, new_value: newValue.trim(), reason: reason.trim() });
      toast.success(`${field} corrected — audited`);
      setNewValue(''); setReason(''); onCorrected?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Correction failed'); } finally { setSaving(false); }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Program</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1.5">
          <p><span className="text-muted-foreground">Teams:</span> {detail.teams.length ? detail.teams.map((t) => t.name).join(', ') : '—'}</p>
          <p><span className="text-muted-foreground">Players managed:</span> {detail.player_count}</p>
          <p><span className="text-muted-foreground">Primary goal:</span> {detail.goals.primary || '—'}</p>
          <p><span className="text-muted-foreground">Linked lead:</span> {detail.linked_lead_id || 'None (direct signup)'}</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Lifecycle</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1.5">
          <p><span className="text-muted-foreground">Registered:</span> {detail.registered_at ? new Date(detail.registered_at).toLocaleDateString() : '—'}</p>
          <p><span className="text-muted-foreground">First value:</span> {detail.first_value_at ? new Date(detail.first_value_at).toLocaleDateString() : 'Not yet'}</p>
          <p><span className="text-muted-foreground">Activated:</span> {detail.activated_at ? new Date(detail.activated_at).toLocaleDateString() : 'Not yet'}</p>
          {detail.tickets_open !== undefined && (
            <p><span className="text-muted-foreground">Open tickets:</span> {detail.tickets_open} of {detail.tickets_total} total</p>
          )}
        </CardContent>
      </Card>
      {canEditStatus && (
        <Card className="sm:col-span-2" data-testid="customer-360-correct-status-card">
          <CardHeader><CardTitle className="text-base">Correct onboarding/account status</CardTitle>
            <CardDescription>spec §16.1 — Customer Success can fix a stuck stage without needing Data Integrity (Owner/Ops only) access.</CardDescription></CardHeader>
          <CardContent className="flex flex-wrap items-end gap-2">
            <select value={field} onChange={(e) => setField(e.target.value)} data-testid="customer-360-correct-field-select"
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="status">Account status</option>
              <option value="lifecycle_stage">Lifecycle stage</option>
            </select>
            <input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="e.g. ACTIVE"
                   data-testid="customer-360-correct-value-input" className="h-9 rounded-md border border-input bg-background px-2 text-sm w-40" />
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
                   data-testid="customer-360-correct-reason-input" className="h-9 rounded-md border border-input bg-background px-2 text-sm flex-1 min-w-[200px]" />
            <Button size="sm" disabled={saving} onClick={save} data-testid="customer-360-correct-save-btn">Save (audited)</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function UsageTab({ detail }) {
  const LABELS = { practice_plans: 'Practice plans created', team_messages: 'Team messages sent', ai_coach: 'Coach AI conversations', teams_created: 'Teams created' };
  return (
    <Card data-testid="customer-360-usage-tab">
      <CardHeader><CardTitle className="text-base">Feature adoption</CardTitle><CardDescription>Counted from the account's event log.</CardDescription></CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(detail.usage).map(([key, v]) => (
            <div key={key} className="rounded-lg border p-3">
              <p className="text-sm font-medium">{LABELS[key] || key}</p>
              <p className="text-2xl font-semibold mt-1">{v.count}</p>
              <p className="text-xs text-muted-foreground">{v.first_used_at ? `First used ${new Date(v.first_used_at).toLocaleDateString()}` : 'Never used'}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TechnicalTab({ detail }) {
  return (
    <Card data-testid="customer-360-technical-tab">
      <CardHeader><CardTitle className="text-base">Platform & sessions</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-3">
        {detail.technical.sessions.length === 0 ? <p className="text-muted-foreground">No session data yet.</p> : (
          detail.technical.sessions.map((s, i) => (
            <p key={i}><span className="text-muted-foreground">{s.platform}:</span> v{s.app_version} · last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : '—'}</p>
          ))
        )}
        <p className="text-xs text-muted-foreground pt-2 border-t">Errors: {detail.technical.errors_note}</p>
      </CardContent>
    </Card>
  );
}

function SupportTab({ accountId, canManage }) {
  const [tickets, setTickets] = useState(null);
  const [comms, setComms] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/tickets`, { params: { account_id: accountId } }).then((r) => setTickets(r.data.tickets));
    axios.get(`${API}/customers/${accountId}/communications`).then((r) => setComms(r.data.communications));
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    if (!resolutionNotes.trim()) { toast.error('Add resolution notes first'); return; }
    try {
      await axios.post(`${API}/tickets/${id}/resolve`, { resolution_notes: resolutionNotes.trim() });
      toast.success('Ticket resolved');
      setResolvingId(null); setResolutionNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resolve'); }
  };

  return (
    <div className="space-y-4" data-testid="customer-360-support-tab">
      <Card>
        <CardHeader><CardTitle className="text-base">Tickets & feedback</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!tickets ? <p className="text-muted-foreground text-sm">Loading…</p> : tickets.length === 0 ? (
            <p className="text-muted-foreground text-sm">No tickets for this account.</p>
          ) : tickets.map((t) => (
            <div key={t.id} className="rounded-lg border p-3 text-sm" data-testid={`customer-360-ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="capitalize">{t.ticket_type}</Badge>
                <Badge variant={t.status === 'RESOLVED' ? 'default' : 'secondary'}>{t.status}</Badge>
              </div>
              <p className="mt-2">{t.description}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.category} · {t.priority} · {new Date(t.created_at).toLocaleString()}</p>
              {t.resolution_notes && <p className="text-xs mt-1 text-emerald-500">Resolved: {t.resolution_notes}</p>}
              {canManage && t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
                resolvingId === t.id ? (
                  <div className="flex gap-2 mt-2">
                    <Textarea value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} rows={2} className="text-xs" />
                    <Button size="sm" onClick={() => resolve(t.id)}>Save</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => setResolvingId(t.id)}>Resolve</Button>
                )
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Communications history</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {!comms ? <p className="text-muted-foreground text-sm">Loading…</p> : comms.length === 0 ? (
            <p className="text-muted-foreground text-sm">No emails sent yet.</p>
          ) : comms.map((c) => (
            <div key={c.job_id} className="flex items-center justify-between text-xs border-b last:border-0 pb-2">
              <span>{c.kind}{c.template ? ` (${c.template})` : ''}</span>
              <span className="text-muted-foreground">{c.status} · {c.completed_at ? new Date(c.completed_at).toLocaleString() : '—'}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function TimelineTab({ accountId }) {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/customers/${accountId}/timeline`).then((r) => setData(r.data)); }, [accountId]);
  return (
    <Card data-testid="customer-360-timeline-tab">
      <CardHeader><CardTitle className="text-base">Timeline</CardTitle><CardDescription>{data ? `${data.total} events` : 'Loading…'}</CardDescription></CardHeader>
      <CardContent className="space-y-2">
        {data?.items.map((e) => (
          <div key={e.id} className="flex items-center justify-between text-xs border-b last:border-0 pb-2">
            <span>{e.name}</span>
            <span className="text-muted-foreground">{new Date(e.occurred_at).toLocaleString()}</span>
          </div>
        ))}
        {data && data.items.length === 0 && <p className="text-muted-foreground text-sm">No events yet.</p>}
      </CardContent>
    </Card>
  );
}

function NotesTab({ accountId, canAddNotes, showSalesAlert, alertPending, onAlertAck }) {
  const [notes, setNotes] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [acking, setAcking] = useState(false);

  const load = useCallback(() => { axios.get(`${API}/customers/${accountId}/notes`).then((r) => setNotes(r.data.notes)); }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const ackAlert = async () => {
    setAcking(true);
    try {
      await axios.post(`${API}/customers/${accountId}/notes/ack-sales-alert`);
      onAlertAck?.();
    } catch (e) { toast.error('Could not dismiss'); } finally { setAcking(false); }
  };

  const add = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/customers/${accountId}/notes`, { body: newNote.trim() });
      setNewNote(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save note'); }
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/customers/${accountId}/notes/${id}`, { body: editBody.trim() });
      setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save edit'); }
  };

  return (
    <Card data-testid="customer-360-notes-tab">
      <CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {showSalesAlert && alertPending && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
               data-testid="sales-note-handoff-banner">
            <span className="text-amber-500">A Sales Admin added a note on this account — review for handoff.</span>
            <Button size="sm" variant="outline" disabled={acking} onClick={ackAlert} data-testid="sales-note-handoff-ack-btn">
              {acking ? 'Dismissing…' : 'Got it'}
            </Button>
          </div>
        )}
        {canAddNotes && (
          <div className="flex gap-2">
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add an internal note…" rows={2} data-testid="customer-360-note-input" />
            <Button onClick={add} data-testid="customer-360-note-add-btn">Add</Button>
          </div>
        )}
        {!notes ? <p className="text-muted-foreground text-sm">Loading…</p> : notes.length === 0 ? (
          <p className="text-muted-foreground text-sm">No notes yet.</p>
        ) : notes.map((n) => (
          <div key={n.id} className="rounded-lg border p-3 text-sm" data-testid={`customer-360-note-${n.id}`}>
            {editingId === n.id ? (
              <div className="space-y-2">
                <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2} />
                <div className="flex gap-2"><Button size="sm" onClick={() => saveEdit(n.id)}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button></div>
              </div>
            ) : (
              <>
                <p>{n.body}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span>{n.author_name}</span>
                    {n.author_role === 'sales_admin' && (
                      <Badge variant="outline" className="text-[10px] py-0 h-4 align-middle">Sales</Badge>
                    )}
                    <span>· {new Date(n.created_at).toLocaleString()}{n.edit_history?.length > 0 && ` · edited ${n.edit_history.length}x`}</span>
                  </div>
                  {canAddNotes && <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setEditingId(n.id); setEditBody(n.body); }}>Edit</Button>}
                </div>
              </>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
